import React from 'react'
import { useAudioMarkerEngine } from './useAudioMarkerEngine'
import UiSlider from '../../../../services/media/components/ui/UiSlider'

type AudioEngine = ReturnType<typeof useAudioMarkerEngine>

interface AudioControlBarProps {
  engine: AudioEngine
}

/**
 * AudioControlBar - Playback controls and volume
 * Reusable pattern from Music Production's MixTransport
 */
export default function AudioControlBar({ engine }: AudioControlBarProps) {
  const progress = engine.duration > 0 ? (engine.currentTime / engine.duration) * 100 : 0

  return (
    <div className="shrink-0 space-y-3">
      {/* Progress bar */}
      <div
        className="h-1 bg-neutral-800 rounded-full cursor-pointer hover:h-2 transition-all group"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const percent = (e.clientX - rect.left) / rect.width
          engine.seek(percent * engine.duration)
        }}
      >
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Transport controls and volume */}
      <div className="flex items-center gap-3">
        {/* Play / Pause button */}
        <button
          onClick={() => (engine.isPlaying ? engine.pause() : engine.play())}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 transition-all text-white"
          aria-label={engine.isPlaying ? 'Pause' : 'Play'}
        >
          {engine.isPlaying ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Stop button */}
        <button
          onClick={() => engine.stop()}
          className="flex items-center justify-center w-9 h-9 rounded-md pac-glass-raised text-neutral-400 hover:text-neutral-200 transition-colors"
          aria-label="Stop"
          title="Stop and reset to start"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 6h12v12H6z" />
          </svg>
        </button>

        {/* Volume control */}
        <div className="flex items-center gap-2 flex-1">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-neutral-400">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
          </svg>
          <UiSlider
            value={engine.volume}
            min={0}
            max={1}
            step={0.01}
            onChange={(v: number) => engine.setVolume(v)}
            className="flex-1 max-w-[120px]"
          />
          <span className="text-xs text-neutral-500 w-8 text-right">
            {Math.round(engine.volume * 100)}%
          </span>
        </div>

        {/* Loop indicator */}
        {engine.activeLoopSection && (
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-900/30 border border-emerald-700/50 rounded text-xs text-emerald-300">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="17 2 22 7 17 12" />
              <path d="M22 7H10a8 8 0 01-8-8" />
              <polyline points="7 22 2 17 7 12" />
              <path d="M2 17h12a8 8 0 018 8" />
            </svg>
            Loop on
          </div>
        )}
      </div>

      {/* Keyboard hint */}
      <p className="text-xs text-neutral-600 text-center">
        Press <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded text-neutral-300 font-mono">Space</kbd> to add marker at current position
      </p>
    </div>
  )
}
